import React, {useState, useEffect, useContext} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Button from '../../components/elements/Button';
import Header from '../../components/elements/Header';
import EditAddressDetails from '../../components/modules/EditAddressDetails';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES} from '../../utils/Constants';
import {
  DLog,
  isEmpty,
  trimSpace,
  validateUSZipCode,
} from '../../utils/HelperMethods';
import {fetchApi} from '../../utils/ApiHelper';
import {ADDRESSES, HTTP_METHODS} from '../../utils/URLConstants';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {tID} from '../../utils/TestIdHelper';
import {CloseIcon} from '../../utils/SvgIcons';

const EditAddress = ({navigation, route, setAddOrEditAddress}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const isEdit = route.params && route.params.addressData ? true : false;
  const editData = isEdit ? route.params.addressData : null;

  const haveAddressName = isEdit && !!editData.addressType;
  const [saveAs, setSaveAs] = useState(
    haveAddressName ? editData.addressType.name : '',
  );
  const [state, setState] = useState(isEdit ? editData.state : '');
  const [city, setCity] = useState(isEdit ? editData.city : '');
  const [address, setAddress] = useState(isEdit ? editData.address1 : '');
  const [zipCode, setZipCode] = useState(isEdit ? editData.zip : '');
  const [aptNote, setAptNote] = useState(isEdit ? editData.address2 : '');
  const [deliveryNote, setDeliveryNote] = useState(
    isEdit ? editData.note || '' : '',
  );

  const [isValid, setIsValid] = useState(false);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validation intentionally keyed to form fields only
  }, [address, city, state, zipCode]);

  const validateForm = () => {
    if (isEmpty(address)) {
      setIsValid(false);
      return;
    } else if (isEmpty(city)) {
      setIsValid(false);
      return;
    } else if (isEmpty(state)) {
      setIsValid(false);
      return;
    } else if (isEmpty(zipCode)) {
      setIsValid(false);
      return;
    } else if (!validateUSZipCode(zipCode)) {
      setIsValid(false);
      return;
    }

    setIsValid(true);
  };

  const onPressCancel = () => {
    if (Platform.OS === 'web') {
      setAddOrEditAddress(false);
    } else {
      navigation.goBack();
    }
  };

  const onPressSave = () => {
    let params = {
      name: trimSpace(saveAs),
      address1: trimSpace(address),
      address2: trimSpace(aptNote),
      city: trimSpace(city),
      state: trimSpace(state),
      zip: trimSpace(zipCode),
      note: trimSpace(deliveryNote),
    };

    if (isEdit) {
      params['id'] = parseInt(editData.id);
    }
    const method = isEdit ? HTTP_METHODS.PUT : HTTP_METHODS.POST;
    lpc.setVisible(true);
    fetchApi(method, ADDRESSES, params)
      .then(response => {
        lpc.setVisible(false);
        if (response.status == 200) {
          if (Platform.OS === 'web') {
            setAddOrEditAddress(false);
          } else {
            navigation.goBack();
          }
        }
      })
      .catch(error => {
        DLog(error);
        lpc.setVisible(false);
        esc.showError(error);
      });
  };

  return (
    <SafeAreaView
      testID={tID('EditAddress-SafeAreaView-Main')}
      style={styles.container}>
      <View
        testID={tID('EditAddress-View-HeaderContainer')}
        style={styles.headerView}>
        <Header
          title={
            Platform.OS === 'web'
              ? `${isEdit ? 'Edit' : 'Add'} Address`
              : 'New Shipping Address'
          }
          leftImage={<CloseIcon />}
          leftIconAccessibilityLabel="Close"
          onPressLeftIcon={
            Platform.OS === 'web'
              ? () => setAddOrEditAddress(false)
              : navigation.goBack
          }
        />
      </View>

      <KeyboardAvoidingView
        testID={tID('EditAddress-KeyboardAvoidingView-1')}
        style={styles.contentContainer}
        behavior={Platform.OS == 'ios' ? 'padding' : null}>
        <ScrollView keyboardShouldPersistTaps="handled"
          testID={tID('EditAddress-ScrollView-1')}
          style={styles.contentContainer}>
          <View
            testID={tID('EditAddress-View-FormContainer')}
            style={styles.typeContainer}>
            <EditAddressDetails
              saveAs={saveAs}
              state={state}
              city={city}
              address={address}
              zipCode={zipCode}
              aptNote={aptNote}
              deliveryNote={deliveryNote}
              setSaveAs={setSaveAs}
              setState={setState}
              setCity={setCity}
              setAddress={setAddress}
              setZipCode={setZipCode}
              setAptNote={setAptNote}
              setDeliveryNote={setDeliveryNote}
            />
          </View>
        </ScrollView>
        <View
          testID={tID('EditAddress-View-ButtonsContainer')}
          style={styles.buttonsContainer}>
          <Button
            testID={tID('EditAddress-Button-Cancel')}
            style={styles.leftButton}
            onPress={onPressCancel}
            title={'Cancel'}
          />
          <Button
            testID={tID('EditAddress-Button-Save')}
            disabled={!isValid}
            style={styles.rightButton}
            onPress={onPressSave}
            title={Platform.OS === 'web' ? 'Save' : 'Save Changes'}
            buttonType={BUTTON_TYPES.Filled}
            titleColor={colors.textOnAccent}
          />
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

export default EditAddress;
